import React, { useState } from 'react';
import {
  Box, Grid, Typography, Paper, TextField, MenuItem, Button, Skeleton, Snackbar, Alert, Tooltip,
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import DownloadIcon from '@mui/icons-material/Download';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell,
} from 'recharts';

import { PageHeader, MetricCard, SkeletonCards, ErrorState, PermissionState, useDesignTokens, FeatureUsageMeter } from '../ui';
import { formatCurrency } from '../../utils/currencyFormatter';
import { radius } from '../../themes/smartinventoryTheme';
import useReportsPage from './useReportsPage';

const pctDelta = (value) => {
  if (value == null) return undefined;
  return { direction: value > 0 ? 'up' : value < 0 ? 'down' : 'neutral', text: `${value > 0 ? '+' : ''}${value}%` };
};

const pointsDelta = (points) => {
  if (points == null) return undefined;
  return { direction: points > 0 ? 'up' : points < 0 ? 'down' : 'neutral', text: `${points > 0 ? '+' : ''}${points}pt` };
};

const REPORT_TYPES = ['sales', 'products', 'customers'];

const Reporting = () => {
  const p = useReportsPage();
  const { tokens: t, categoryColors } = useDesignTokens();
  const [csvSnackbar, setCsvSnackbar] = useState(false);
  const palette = Object.values(categoryColors).filter((tone) => tone.dot !== categoryColors.none.dot).map((tone) => tone.dot);

  if (p.effectiveStatus === 'loading') {
    return (
      <Box>
        <SkeletonCards rows={1} />
        <Grid container spacing={2} sx={{ mb: 2, mt: 1 }}>
          {[0, 1, 2, 3].map((i) => <Grid item xs={6} md={3} key={i}><Skeleton variant="rounded" height={100} sx={{ borderRadius: `${radius.lg}px` }} /></Grid>)}
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}><Skeleton variant="rounded" height={260} sx={{ borderRadius: `${radius.xl}px` }} /></Grid>
          <Grid item xs={12} md={4}><Skeleton variant="rounded" height={260} sx={{ borderRadius: `${radius.xl}px` }} /></Grid>
        </Grid>
      </Box>
    );
  }

  if (p.effectiveStatus === 'error') {
    return (
      <ErrorState
        title={p.t("Couldn't load report data", { defaultValue: "Couldn't load report data" })}
        description={p.t('The server did not respond. Your data is safe — nothing was changed.', { defaultValue: 'The server did not respond. Your data is safe — nothing was changed.' })}
        request={p.errorInfo?.request}
        retryLabel={p.t('Try again', { defaultValue: 'Try again' })}
        onRetry={p.fetchMetrics}
      />
    );
  }

  if (p.effectiveStatus === 'permission') {
    return (
      <PermissionState
        title={p.t("You don't have access to Reports", { defaultValue: "You don't have access to Reports" })}
        description={p.t('Ask your workspace owner for access.', { defaultValue: 'Ask your workspace owner for access.' })}
      />
    );
  }

  const d = p.data;
  const donutData = d.salesByCategory.map((c, i) => ({ ...c, color: palette[i % palette.length] }));
  const statusBarColorFor = { pending: t.warning, accepted: t.accent, rejected: t.error, paid: t.success };

  return (
    <Box>
      <PageHeader
        title={p.t('Reports', { defaultValue: 'Reports' })}
        subtitle={p.t('Sales and inventory analytics', { defaultValue: 'Sales and inventory analytics' })}
        actions={[
          { label: p.t('Export CSV', { defaultValue: 'Export CSV' }), icon: DownloadIcon, variant: 'outlined', onClick: () => setCsvSnackbar(true) },
        ]}
        helpKey="reporting"
        meter={<FeatureUsageMeter feature="reports.generated.monthly" label={p.t('Reports generated this month', { defaultValue: 'Reports generated this month' })} />}
      />

      <Paper data-tour="reports-filters" variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField select size="small" label={p.t('Report type', { defaultValue: 'Report type' })} value={p.reportType} onChange={(e) => p.setReportType(e.target.value)} sx={{ minWidth: 180 }}>
          {REPORT_TYPES.map((type) => (
            <MenuItem key={type} value={type}>{p.t(`report.type.${type}`, { defaultValue: type.charAt(0).toUpperCase() + type.slice(1) })}</MenuItem>
          ))}
        </TextField>
        <TextField size="small" type="date" label={p.t('From', { defaultValue: 'From' })} value={p.fromInput} onChange={(e) => p.setFromInput(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField size="small" type="date" label={p.t('To', { defaultValue: 'To' })} value={p.toInput} onChange={(e) => p.setToInput(e.target.value)} InputLabelProps={{ shrink: true }} />
        {p.planLimit?.atLimit ? (
          <Tooltip title={p.planLimit.tooltip}>
            <span>
              <Button data-tour="reports-run" variant="contained" startIcon={<PlayArrowIcon />} disabled>{p.t('Run report', { defaultValue: 'Run report' })}</Button>
            </span>
          </Tooltip>
        ) : (
          <Button data-tour="reports-run" variant="contained" startIcon={<PlayArrowIcon />} onClick={p.runReport}>{p.t('Run report', { defaultValue: 'Run report' })}</Button>
        )}
      </Paper>

      {d.insight && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: 'primary.light', borderColor: 'primary.main' }}>
          <EmojiEventsIcon sx={{ color: 'primary.main' }} />
          <Typography variant="body2" fontWeight={600}>{d.insight}</Typography>
        </Paper>
      )}

      <Grid container spacing={1.75} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}>
          <MetricCard label={p.t('Revenue')} value={formatCurrency(d.kpis.revenue.value)} delta={pctDelta(d.kpis.revenue.delta)} sparkline={d.kpis.revenue.sparkline} />
        </Grid>
        <Grid item xs={6} md={3}>
          <MetricCard label={p.t('Orders')} value={d.kpis.orders.value} delta={pctDelta(d.kpis.orders.delta)} sparkline={d.kpis.orders.sparkline} />
        </Grid>
        <Grid item xs={6} md={3}>
          <MetricCard label={p.t('Avg. order value', { defaultValue: 'Avg. order value' })} value={formatCurrency(d.kpis.avgOrderValue.value)} delta={pctDelta(d.kpis.avgOrderValue.delta)} sparkline={d.kpis.avgOrderValue.sparkline} />
        </Grid>
        <Grid item xs={6} md={3}>
          <MetricCard label={p.t('Repeat buyers', { defaultValue: 'Repeat buyers' })} value={`${d.kpis.repeatRate.value}%`} delta={pointsDelta(d.kpis.repeatRate.deltaPoints)} sparkline={d.kpis.repeatRate.sparkline} />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={8}>
          <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography variant="subtitle1">{p.t('Revenue · daily', { defaultValue: 'Revenue · daily' })}</Typography>
              <Typography variant="body2" fontWeight={700}>{formatCurrency(d.kpis.revenue.value)}</Typography>
            </Box>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={d.revenueDaily} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="reportsRevenueFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={t.accent} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={t.accent} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} stroke={t.border} />
                <XAxis dataKey="date" tick={{ fontSize: 10.5, fill: t.muted }} tickFormatter={(v) => new Date(v).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} axisLine={{ stroke: t.border }} tickLine={false} minTickGap={40} />
                {/* Compact ticks ("1.2K") — full "$1,200.00" labels overflow the
                    44px axis on phones and get clipped to "200.00". The tooltip
                    still shows the exact currency value. */}
                <YAxis tick={{ fontSize: 10.5, fill: t.muted }} axisLine={false} tickLine={false} width={44} tickFormatter={(v) => new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(v)} />
                <RechartsTooltip formatter={(value) => formatCurrency(value)} labelFormatter={(v) => new Date(v).toLocaleDateString()} contentStyle={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: radius.md, fontSize: 12.5 }} />
                <Area type="monotone" dataKey="value" stroke={t.accent} strokeWidth={2} fill="url(#reportsRevenueFill)" />
              </AreaChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
            <Typography variant="subtitle1" sx={{ mb: 1.5 }}>{p.t('Sales by category', { defaultValue: 'Sales by category' })}</Typography>
            {donutData.length === 0 ? (
              <Typography variant="body2" color="text.secondary">{p.t('No sales in this period.', { defaultValue: 'No sales in this period.' })}</Typography>
            ) : (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <ResponsiveContainer width="100%" height={140}>
                    <PieChart>
                      <Pie data={donutData} dataKey="value" nameKey="name" innerRadius={44} outerRadius={64} paddingAngle={2}>
                        {donutData.map((entry, i) => <Cell key={entry.name} fill={entry.color} />)}
                      </Pie>
                      <RechartsTooltip formatter={(value) => formatCurrency(value)} contentStyle={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: radius.md, fontSize: 12.5 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
                {donutData.map((c) => (
                  <Box key={c.name} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.4 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: `${radius.xs}px`, bgcolor: c.color, flexShrink: 0 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }} noWrap>{c.name}</Typography>
                    <Typography variant="body2" fontWeight={600}>{formatCurrency(c.value)}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ width: 40, textAlign: 'right' }}>{c.share}%</Typography>
                  </Box>
                ))}
              </>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
            <Typography variant="subtitle1" sx={{ mb: 1.5 }}>{p.t('Top products by revenue', { defaultValue: 'Top products by revenue' })}</Typography>
            {d.topProducts.length === 0 ? (
              <Typography variant="body2" color="text.secondary">{p.t('No product sales in this period.', { defaultValue: 'No product sales in this period.' })}</Typography>
            ) : d.topProducts.map((prod) => (
              <Box key={prod.productId || prod.name} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.25 }}>
                <Typography variant="body2" color="text.secondary" sx={{ width: 130, flexShrink: 0 }} noWrap>{prod.name}</Typography>
                <Box sx={{ flex: 1, height: 14, borderRadius: `${radius.sm}px`, bgcolor: 'action.selected', overflow: 'hidden' }}>
                  <Box sx={{ width: `${prod.share}%`, height: '100%', bgcolor: 'primary.main' }} />
                </Box>
                <Typography variant="body2" fontWeight={700} sx={{ width: 64, textAlign: 'right', flexShrink: 0 }}>{formatCurrency(prod.revenue)}</Typography>
              </Box>
            ))}
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
            <Typography variant="subtitle1" sx={{ mb: 1.25 }}>{p.t('Best customers', { defaultValue: 'Best customers' })}</Typography>
            {d.topCustomers.length === 0 ? (
              <Typography variant="body2" color="text.secondary">{p.t('No customer activity in this period.', { defaultValue: 'No customer activity in this period.' })}</Typography>
            ) : d.topCustomers.map((c, index) => (
              <Box key={c.userId || c.name} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 1, borderTop: index > 0 ? '1px solid' : 'none', borderColor: 'divider' }}>
                <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: 'action.selected', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary', flexShrink: 0 }} fontSize={10.5} fontWeight={600}>
                  {c.name ? c.name.trim().charAt(0).toUpperCase() : '?'}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} noWrap>{c.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{p.t('{{count}} orders', { count: c.orders, defaultValue: `${c.orders} orders` })}</Typography>
                </Box>
                <Typography variant="body2" fontWeight={700}>{formatCurrency(c.revenue)}</Typography>
              </Box>
            ))}
          </Paper>
        </Grid>

        <Grid item xs={12} md={3}>
          <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
            <Typography variant="subtitle1" sx={{ mb: 1.5 }}>{p.t('Orders by status', { defaultValue: 'Orders by status' })}</Typography>
            <Box sx={{ display: 'flex', height: 12, borderRadius: `${radius.sm}px`, overflow: 'hidden', mb: 1.5 }}>
              {d.ordersByStatus.map((row) => (
                <Box key={row.status} sx={{ width: `${row.share}%`, bgcolor: statusBarColorFor[row.status] || 'action.selected' }} />
              ))}
            </Box>
            {d.ordersByStatus.map((row) => (
              <Box key={row.status} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.4 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: `${radius.xs}px`, bgcolor: statusBarColorFor[row.status] || 'action.selected', flexShrink: 0 }} />
                <Typography variant="body2" color="text.secondary" sx={{ flex: 1, textTransform: 'capitalize' }}>{p.t(row.status)}</Typography>
                <Typography variant="body2" fontWeight={600}>{row.count}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ width: 34, textAlign: 'right' }}>{row.share}%</Typography>
              </Box>
            ))}
            <Box sx={{ borderTop: '1px solid', borderColor: 'divider', mt: 1.25, pt: 1.25 }}>
              <Typography variant="caption" color="text.secondary">{p.t('Fulfilment rate', { defaultValue: 'Fulfilment rate' })}</Typography>
              <Typography variant="h6" sx={{ color: 'success.main' }}>{d.fulfilmentRate}%</Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Snackbar open={csvSnackbar} autoHideDuration={4000} onClose={() => setCsvSnackbar(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="info" sx={{ width: '100%' }}>{p.t("CSV export isn't available for this report yet.", { defaultValue: "CSV export isn't available for this report yet." })}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Reporting;
